import React from 'react';
import { Paper, Typography, CircularProgress, Alert, Box, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { LineChart, DonutChart, BaseChart, StatTile } from '@personal-dashboard/ui';
import { GET_CUSTOM_WIDGET_DATA } from '../queries';
import { buildWidgetSeries, FieldDefinition } from '../utils/widgetSeries';
import { buildRandomSeries } from '../utils/randomSeries';

interface WidgetLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  minH: number;
}

export interface DashboardWidgetData {
  id: string;
  title: string;
  fieldId: string;
  sectionId?: string;
  chartType: 'LINE' | 'BAR' | 'DONUT' | 'STAT';
  aggregation: 'LATEST' | 'SUM' | 'AVG' | 'COUNT' | 'PERCENT_TRUE';
  layout: WidgetLayout;
  visible: boolean;
  orderIndex: number;
  isSystem?: boolean;
  systemKey?: string;
}

interface CustomChartWidgetProps {
  widget: DashboardWidgetData;
  daysCount: number;
  preview?: boolean;
  previewField?: FieldDefinition;
}

const CustomChartWidget: React.FC<CustomChartWidgetProps> = ({ widget, daysCount, preview, previewField }) => {
  const theme = useTheme();

  const dateRange = {
    start: dayjs().subtract(daysCount, 'day').startOf('day').toISOString(),
    end: dayjs().endOf('day').toISOString(),
  };

  const { data, loading, error } = useQuery(GET_CUSTOM_WIDGET_DATA, {
    variables: { dateRange },
    fetchPolicy: 'cache-and-network',
    skip: preview,
  });

  if (loading && !preview) {
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (error && !preview) {
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
        <Alert severity="error">{error.message}</Alert>
      </Paper>
    );
  }

  let sections: any[];
  let fieldDef: FieldDefinition | undefined;

  if (preview) {
    sections = [];
    fieldDef = previewField;
  } else {
    sections = data?.getCustomFieldSections || [];
    for (const section of sections) {
      const found = (section.fields || []).find((f: any) => f.id === widget.fieldId);
      if (found) {
        fieldDef = { id: found.id, name: found.name, type: found.type, options: found.options };
        break;
      }
    }
  }

  if (!fieldDef) {
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
        <Alert severity="warning">Source field no longer exists.</Alert>
      </Paper>
    );
  }

  const days = preview ? [] : (data?.getDays || []);
  const result = preview
    ? buildRandomSeries(fieldDef, daysCount, widget.chartType, widget.aggregation, widget.id)
    : buildWidgetSeries(fieldDef, days, daysCount, widget.chartType, widget.aggregation);

  if (result.kind === 'empty') {
    return (
      <Paper sx={{ p: 3, height: '100%', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No chart available for this field type.</Typography>
      </Paper>
    );
  }

  if (result.kind === 'stat') {
    return (
      <StatTile
        value={result.statValue}
        subtitle={result.statSubtitle}
        height="100%"
      />
    );
  }

  if (result.kind === 'donut') {
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
        <DonutChart seriesName={fieldDef.name} data={result.donutData || []} height="100%" />
      </Paper>
    );
  }

  if (result.kind === 'bar') {
    const option = {
      tooltip: { trigger: 'axis' },
      legend: (result.series || []).length > 1 ? { data: (result.series || []).map((s) => s.name) } : undefined,
      xAxis: { type: 'category', data: result.categories },
      yAxis: {
        type: 'value',
        min: result.yAxisMin,
        max: result.yAxisMax,
        axisLabel: result.yAxisFormatter ? { formatter: result.yAxisFormatter } : undefined,
      },
      series: (result.series || []).map((s) => ({
        name: s.name,
        type: 'bar',
        data: s.data,
        itemStyle: { color: theme.palette.primary.main, borderRadius: [4, 4, 0, 0] },
      })),
    };
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
        <Box sx={{ height: '100%' }}>
          <BaseChart option={option} height="100%" />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
      <LineChart
        categories={result.categories || []}
        series={(result.series || []).map((s) => ({ name: s.name, data: s.data as number[] }))}
        yAxisFormatter={result.yAxisFormatter}
        height="100%"
      />
    </Paper>
  );
};

export default CustomChartWidget;
